/* eslint-disable sort-imports */
/**
 * Upload review — what will be saved: the recipe standards grouped by
 * product (one table per standard), and a job-order PDF's operations with
 * editable names.
 */
import {
	Box,
	Chip,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import React, { useCallback } from "react";

import { JobOrderOperation, ParsedRecipeOperation, ParsedRecipeStandard } from "../../../../services/labor-standards-api";
import { MonoText, planner,SectionLabel } from "../../planner-ui";
import { formatCrew, isMachinePhase } from "../../utils/standard-matching";
import { formatHours,MasterJob,RecipeProductGroup,ResolveWorkstation } from "../utils/recipe-mapping";
import {
	chipMonoSx,
	chipSx,
	fieldSx,
	monoTableCellSx,
	panelSx,
	statusChipSx,
	tableCellSx,
	tableContainerSx,
	tableHeadCellSx,
} from "./upload-styles";

// ---------------------------------------------------------------------------
// Recipe standards
// ---------------------------------------------------------------------------

const metaMono = { ...planner.type.meta, fontFamily: planner.fontMono, color: planner.inkMuted } as const;

const RecipeOperationRow: React.FC<{
	op: ParsedRecipeOperation;
	resolveWorkstation: ResolveWorkstation;
	workstationRoles: Record<string, number | "">;
	jobRoles: MasterJob[];
}> = ({ op, resolveWorkstation, workstationRoles, jobRoles }) => {
	const workstation = resolveWorkstation(op.rawWorkstation, op.workstation);
	const roleId = workstationRoles[workstation || op.rawWorkstation];
	const roleName = roleId ? jobRoles.find((jr) => jr.id === roleId)?.name : undefined;
	const alternatives = op.workstationOptions
		.map((option) => resolveWorkstation(option.rawWorkstation, option.workstation) || option.rawWorkstation)
		.filter((name) => name && name !== workstation);
	return (
		<TableRow hover>
			<TableCell sx={monoTableCellSx}>{op.code || op.sequence}</TableCell>
			<TableCell sx={tableCellSx}>{op.name}</TableCell>
			<TableCell sx={tableCellSx}>
				{workstation ? (
					<Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
						<Chip label={workstation} size="small" sx={chipSx} />
						{alternatives.length > 0 ? (
							<Typography component="span" sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted }}>
								or {alternatives.join(", ")}
							</Typography>
						) : null}
						{workstation !== op.rawWorkstation ? (
							<Typography component="span" sx={metaMono}>
								({op.rawWorkstation})
							</Typography>
						) : null}
					</Box>
				) : (
					<Typography component="span" sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.partial }}>
						{op.rawWorkstation} — not mapped
					</Typography>
				)}
			</TableCell>
			<TableCell sx={tableCellSx}>
				{op.phases.map((phase, phaseIdx) => {
					// Machine time: the unit is busy, nobody is needed — no role to pick.
					if (isMachinePhase(phase)) {
						return (
							<Box key={`machine-${phase.workPhaseCode ?? ""}-${phase.startOffsetMinutes}-${phaseIdx}`} display="flex" alignItems="center" gap={0.75}>
								<Chip label={`Machine · ${formatHours(phase.durationMinutes / 60)}`} size="small" sx={chipSx} />
								{phase.workPhaseCode ? (
									<Typography component="span" sx={metaMono}>
										{phase.workPhaseCode}
									</Typography>
								) : null}
								{phase.startOffsetMinutes ? (
									<Typography component="span" sx={metaMono}>
										+{phase.startOffsetMinutes} min
									</Typography>
								) : null}
							</Box>
						);
					}
					const label = phase.jobRole || roleName;
					return (
						<Box key={`crew-${phase.jobRole ?? ""}-${phase.startOffsetMinutes}-${phaseIdx}`} display="flex" alignItems="center" gap={0.75}>
							<Box component="span" sx={{ fontFamily: planner.fontMono }}>
								{formatCrew(phase.workersRequired)}×
							</Box>
							{label ? (
								label
							) : (
								<Typography component="span" sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.partial }}>
									Pick a job role
								</Typography>
							)}
							{phase.startOffsetMinutes ? (
								<Typography component="span" sx={metaMono}>
									+{phase.startOffsetMinutes} min
								</Typography>
							) : null}
						</Box>
					);
				})}
			</TableCell>
			<TableCell align="right" sx={monoTableCellSx}>
				{formatHours(op.durationMinutes / 60)}
			</TableCell>
		</TableRow>
	);
};

const RecipeStandardPanel: React.FC<{
	std: ParsedRecipeStandard;
	resolveWorkstation: ResolveWorkstation;
	workstationRoles: Record<string, number | "">;
	jobRoles: MasterJob[];
}> = ({ std, resolveWorkstation, workstationRoles, jobRoles }) => {
	const isUpdate = Boolean(std.existingStandardId);
	return (
		<Box sx={{ ml: 1, mb: 2, pl: 1.5, borderLeft: `3px solid ${planner.accentBorder}` }}>
			<Box display="flex" gap={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
				<Chip label={std.recipeGroup ? `Recipe ${std.recipeGroup}` : "Recipe"} sx={chipSx} size="small" />
				<Chip label={`${std.batchSize.toLocaleString()} ${std.batchUnit || "units"}`} sx={chipMonoSx} size="small" />
				<Chip
					label={isUpdate ? "Will update" : "Will create"}
					size="small"
					sx={isUpdate ? statusChipSx(planner.partial, planner.partialSoft) : statusChipSx(planner.staffed, planner.staffedSoft)}
				/>
				{std.description ? (
					<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted }}>{std.description}</Typography>
				) : null}
			</Box>

			<TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={tableHeadCellSx}>#</TableCell>
							<TableCell sx={tableHeadCellSx}>Operation</TableCell>
							<TableCell sx={tableHeadCellSx}>Work centre</TableCell>
							<TableCell sx={tableHeadCellSx}>Crew</TableCell>
							<TableCell align="right" sx={tableHeadCellSx}>
								Duration
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{[...std.operations]
							.sort((a, b) => a.sequence - b.sequence)
							.map((op) => (
								<RecipeOperationRow
									key={`${op.sequence}-${op.code ?? op.name}`}
									op={op}
									resolveWorkstation={resolveWorkstation}
									workstationRoles={workstationRoles}
									jobRoles={jobRoles}
								/>
							))}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};

export const RecipePreview: React.FC<{
	groups: RecipeProductGroup[];
	standardCount: number;
	updateCount: number;
	resolveWorkstation: ResolveWorkstation;
	workstationRoles: Record<string, number | "">;
	jobRoles: MasterJob[];
}> = ({ groups, standardCount, updateCount, resolveWorkstation, workstationRoles, jobRoles }) => (
	<>
		{groups.length > 0 && (
			<Typography sx={{ ...planner.type.pageTitle, fontSize: 17, color: planner.navy, mb: 2 }}>
				Standards to save ({standardCount})
				{updateCount > 0 ? (
					<Box component="span" sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.inkMuted, ml: 1 }}>
						{updateCount} will update an existing standard
					</Box>
				) : null}
			</Typography>
		)}

		{groups.map((group) => (
			<Paper key={group.productId ?? group.productName} elevation={0} sx={{ ...panelSx, mb: 2 }}>
				<Box display="flex" gap={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap">
					<Typography sx={{ ...planner.type.title, fontFamily: planner.fontUi, color: planner.ink }}>{group.productName}</Typography>
					<Chip label={group.standards[0]?.productKey} sx={chipMonoSx} size="small" />
					{group.productId == null && (
						<Chip label="No matching product — saved by SKU" size="small" sx={statusChipSx(planner.partial, planner.partialSoft)} />
					)}
				</Box>

				{group.standards.map((std) => (
					<RecipeStandardPanel
						key={`${std.productKey}-${std.recipeGroup ?? "recipe"}-${std.batchSize}`}
						std={std}
						resolveWorkstation={resolveWorkstation}
						workstationRoles={workstationRoles}
						jobRoles={jobRoles}
					/>
				))}
			</Paper>
		))}
	</>
);

// ---------------------------------------------------------------------------
// Job-order PDF operations
// ---------------------------------------------------------------------------

const PdfOperationPanel: React.FC<{
	op: JobOrderOperation;
	name: string;
	phaseRoles: Record<string, string>;
	onNameChange: (sequence: number, name: string) => void;
}> = ({ op, name, phaseRoles, onNameChange }) => {
	const handleName = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => onNameChange(op.sequence, event.target.value),
		[onNameChange, op.sequence]
	);
	return (
		<Paper elevation={0} sx={{ ...panelSx, mb: 2 }}>
			<Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
				<MonoText sx={{ color: planner.inkMuted, fontSize: 13, minWidth: 24 }}>{op.sequence}</MonoText>
				<TextField
					size="small"
					label="Operation name"
					value={name}
					onChange={handleName}
					sx={{ ...fieldSx, flex: "1 1 260px", minWidth: 240 }}
				/>
				{op.code && <Chip label={op.code} size="small" sx={chipMonoSx} />}
				<Box sx={{ ml: "auto", textAlign: "right" }}>
					<SectionLabel sx={{ textAlign: "right" }}>Total</SectionLabel>
					<Typography sx={{ ...planner.type.stat, fontFamily: planner.fontMono, fontSize: 15, color: planner.accentDeep }}>
						{formatHours(op.totalHours)}
					</Typography>
				</Box>
			</Box>

			{op.notes && (
				<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted, mt: 1 }}>{op.notes}</Typography>
			)}

			<TableContainer component={Paper} elevation={0} sx={{ mt: 1.5, ...tableContainerSx }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={tableHeadCellSx}>Phase</TableCell>
							<TableCell sx={tableHeadCellSx}>Description</TableCell>
							<TableCell sx={tableHeadCellSx}>Job Role</TableCell>
							<TableCell align="right" sx={tableHeadCellSx}>
								Hours
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{op.phases.map((phase, phaseIdx) => (
							<TableRow key={`${op.sequence}-${phase.phaseCode}-${phaseIdx}`} hover>
								<TableCell sx={monoTableCellSx}>{phase.phaseCode}</TableCell>
								<TableCell sx={tableCellSx}>{phase.description || "—"}</TableCell>
								<TableCell sx={tableCellSx}>
									{phaseRoles[phase.phaseCode] || (
										<Typography component="span" sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.partial }}>
											Not mapped
										</Typography>
									)}
								</TableCell>
								<TableCell align="right" sx={monoTableCellSx}>
									{formatHours(phase.hours)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	);
};

export const PdfOperationsPreview: React.FC<{
	operations: JobOrderOperation[];
	operationNames: Record<number, string>;
	phaseRoles: Record<string, string>;
	onNameChange: (sequence: number, name: string) => void;
}> = ({ operations, operationNames, phaseRoles, onNameChange }) => (
	<>
		<Typography sx={{ ...planner.type.pageTitle, fontSize: 17, color: planner.navy, mb: 1.5 }}>
			Operations ({operations.length})
		</Typography>
		{[...operations]
			.sort((a, b) => a.sequence - b.sequence)
			.map((op) => (
				<PdfOperationPanel
					key={op.sequence}
					op={op}
					name={operationNames[op.sequence] ?? op.name}
					phaseRoles={phaseRoles}
					onNameChange={onNameChange}
				/>
			))}
	</>
);
